import type { AreaFeature, HealthResponse, StatusAreaSummary, StatusResponse } from "./apiTypes";
import { formatCountdownShort, formatManilaDateTime, formatRelativeTime } from "./countdown";
import {
  computeGlance,
  CONDITION_METADATA,
  GLANCE_CONDITIONS,
  GLANCE_ORDER,
  GLANCE_TIERS,
  isGlanceTier,
  matchesFilters,
  type Glance,
  type GlanceCondition,
  type GlanceTier,
} from "./glance";

type QueryRoot = Document | DocumentFragment | HTMLElement;

export const VECO_FACEBOOK_PAGE_URL = "https://www.facebook.com/visayanelectriccompany";

/** Plain-language glossary (R-611) for the floating legend. */
export const CONDITION_DEFINITIONS: Record<GlanceCondition, string> = {
  off: "Power is off right now, or a possible rotational window covers this moment. Ends at the time shown.",
  soon: "Power is on, but an announced window starts within the next 3 hours.",
  later: "Power is on now. VECO has announced a window later today or on a later day.",
  clear: "Power is on and no advisory mentions this area for the coming days.",
  unknown: "An advisory mentions this area but the status could not be read from it.",
  restored: "VECO said power is back today, or today's window has ended. A later window still paints the area by that window.",
  cancelled: "VECO cancelled an announced interruption for this area.",
};

const SVG_ICONS = {
  search:
    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2"/><path d="M16.5 16.5 21 21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  locate:
    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="2.5" fill="currentColor"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  bolt: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor"/></svg>',
};

function legendItemsHtml(): string {
  return GLANCE_CONDITIONS.map((condition) => {
    const metadata = CONDITION_METADATA[condition];
    const tier = isGlanceTier(condition);
    const swatch = tier ? `legend-swatch tier-${condition}` : `legend-swatch legend-tag-swatch tag-${condition}`;
    return `<label class="legend-item${tier ? "" : " legend-tag"}" data-status-row="${condition}" data-tier="${condition}">
      <input type="checkbox" id="status-filter-${condition}" name="status" data-status-filter value="${condition}" />
      <span class="${swatch}" aria-hidden="true"></span>
      <span class="legend-text"><span class="legend-label">${metadata.label}</span><span class="legend-sub">${metadata.sublabel}</span></span>
      <span class="sr-only">, ${metadata.cue}</span>
      <span class="legend-count" data-status-count="${condition}">0</span>
    </label>`;
  }).join("");
}

export function legendHelpHtml(): string {
  const items = GLANCE_CONDITIONS.map((condition) => {
    return `<dt>${CONDITION_METADATA[condition].label}</dt><dd>${CONDITION_DEFINITIONS[condition]}</dd>`;
  }).join("");
  const estimated =
    "<dt>Estimated area</dt><dd>Traced automatically from VECO's map image; edges can be off by a block or more. Open VECO's map from the area panel to check.</dd>";
  return `<details class="legend-help" data-legend-help><summary class="legend-help-toggle">What do these mean?</summary><dl>${items}${estimated}</dl></details>`;
}

export const APP_SHELL_HTML = `
  <a class="skip-link" href="#selected-area-panel">Skip to area details</a>
  <section class="stale-banner" data-stale-banner hidden aria-live="polite"></section>
  <section class="api-error" data-api-error hidden role="alert">
    <p>Map data could not load.</p>
    <button type="button" data-retry-button>Retry</button>
  </section>
  <div class="app-layout" data-app-shell data-sheet-state="collapsed">
    <section class="search-shell" aria-label="Search areas and addresses">
      <label class="sr-only" for="address-search">Search for an outage area or an address in Cebu</label>
      <span class="search-icon" aria-hidden="true">${SVG_ICONS.search}</span>
      <input id="address-search" data-search-input type="search" autocomplete="off" placeholder="Search an area or address" />
      <button type="button" class="locate-button" data-locate-button aria-describedby="locate-status">${SVG_ICONS.locate}<span class="locate-label">Locate me</span></button>
      <p id="search-status" class="status-message visually-hidden" data-search-status aria-live="polite"></p>
      <p id="locate-status" class="status-message visually-hidden" data-locate-status aria-live="polite"></p>
      <div class="search-results" data-search-results hidden></div>
    </section>
    <section class="legend" data-legend data-legend-open="false" data-filter-active="false" aria-labelledby="legend-title">
      <button type="button" class="legend-toggle" data-legend-toggle aria-expanded="false" aria-controls="legend-body">
        <span class="legend-summary" data-legend-summary></span>
        <span class="legend-toggle-label">Legend &amp; filters</span>
        <span class="legend-toggle-chevron" aria-hidden="true">▴</span>
      </button>
      <div class="legend-body" id="legend-body">
        <h2 id="legend-title" class="legend-title">At a glance</h2>
        <p class="legend-hint">Select one or more to show only those areas.</p>
        <div class="legend-list" role="group" aria-label="Show only areas that are">${legendItemsHtml()}</div>
        <button type="button" class="legend-clear" data-legend-clear hidden>Clear filters</button>
        ${legendHelpHtml()}
      </div>
    </section>
    <aside class="details-panel bottom-sheet" data-sidebar data-bottom-sheet data-state="collapsed" aria-expanded="false" aria-labelledby="sheet-title">
      <button type="button" class="sheet-grabber" data-sheet-grabber aria-label="Panel size: collapsed. Use arrow keys to resize.">
        <span class="sheet-handle" aria-hidden="true"></span>
      </button>
      <button type="button" class="sheet-summary-row" data-sheet-summary-toggle aria-label="Open outage details">
        <span class="sheet-summary-text" data-sheet-summary-text><span class="sheet-summary-name" data-sheet-summary-name>0 off now · 0 soon · 0 later</span><span class="sheet-summary-status" data-sheet-summary-status></span></span>
      </button>
      <div class="sheet-content" data-sheet-content>
        <h1 id="sheet-title"><span class="title-mark" aria-hidden="true">${SVG_ICONS.bolt}</span>VECO Outage Map</h1>
        <p class="unofficial-note" data-unofficial-note>Unofficial. Not affiliated with Visayan Electric Company. Statuses are parsed from VECO's public Facebook advisories and its online interruption calendar, which sometimes run early, late, or wrong.</p>
        <p class="freshness" data-freshness aria-live="polite"></p>
        <section id="selected-area-panel" data-area-panel class="selected-area" aria-live="polite" tabindex="-1" hidden>
          <h2>Selected area</h2>
          <p class="muted">Choose a polygon or area from the list.</p>
        </section>
        <div class="global-views" data-global-views>
          <section class="upnext-section" aria-labelledby="upnext-title">
            <div class="section-head">
              <h2 id="upnext-title">Up next</h2>
              <p class="section-sub">Every announced window, soonest first. Open a slot to see its areas.</p>
            </div>
            <div class="upnext" data-upnext><p class="muted">Loading outage windows...</p></div>
          </section>
          <details class="area-directory" data-area-directory>
            <summary class="area-directory-toggle">
              <h2 id="area-list-title">All areas</h2>
              <span class="area-directory-count" data-area-directory-count></span>
            </summary>
            <input type="search" id="area-filter" name="areaFilter" class="area-filter" data-area-filter aria-label="Filter areas by name" placeholder="Filter areas by name" />
            <div data-area-list class="area-list" role="list"></div>
            <section data-unmapped-panel class="unmapped-panel" aria-labelledby="unmapped-title">
              <h2 id="unmapped-title">Unmapped areas</h2>
              <p class="muted">Unmapped advisories will appear here.</p>
            </section>
          </details>
        </div>
        <section data-unmatched-panel class="unmatched-panel-shell" aria-live="polite"></section>
        <section id="health-panel" data-health-panel class="health-panel">
          <h2>Health details</h2>
          <p class="muted">Loading health status...</p>
        </section>
        <p class="map-credit">Map data © OpenStreetMap contributors</p>
      </div>
    </aside>
    <main class="map-pane">
      <div id="map" class="map" data-map role="region" aria-label="Map of Visayan Electric outage areas" tabindex="0"></div>
    </main>
  </div>`;

export interface AppElements {
  root: HTMLElement;
  map: HTMLElement;
  searchInput: HTMLInputElement;
  searchStatus: HTMLElement;
  searchResults: HTMLElement;
  locateButton: HTMLButtonElement;
  locateStatus: HTMLElement;
  sidebar: HTMLElement;
  bottomSheet: HTMLElement;
  sheetSummaryText: HTMLElement;
  legend: HTMLElement;
  legendToggle: HTMLButtonElement;
  globalViews: HTMLElement;
  freshness: HTMLElement;
  areaPanel: HTMLElement;
  upNext: HTMLElement;
  areaList: HTMLElement;
  unmappedPanel: HTMLElement;
  unmatchedPanel: HTMLElement;
  staleBanner: HTMLElement;
  healthPanel: HTMLElement;
  apiError: HTMLElement;
  retryButton: HTMLButtonElement;
}

function mustQuery<T extends globalThis.Element>(root: QueryRoot, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing frontend shell element: ${selector}`);
  }
  return element;
}

export function ensureAppShell(root: HTMLElement): AppElements {
  if (!root.querySelector("[data-app-shell]")) {
    root.innerHTML = APP_SHELL_HTML;
  }

  return {
    root,
    map: mustQuery(root, "[data-map]"),
    searchInput: mustQuery(root, "[data-search-input]"),
    searchStatus: mustQuery(root, "[data-search-status]"),
    searchResults: mustQuery(root, "[data-search-results]"),
    locateButton: mustQuery(root, "[data-locate-button]"),
    locateStatus: mustQuery(root, "[data-locate-status]"),
    sidebar: mustQuery(root, "[data-sidebar]"),
    bottomSheet: mustQuery(root, "[data-bottom-sheet]"),
    sheetSummaryText: mustQuery(root, "[data-sheet-summary-text]"),
    legend: mustQuery(root, "[data-legend]"),
    legendToggle: mustQuery(root, "[data-legend-toggle]"),
    globalViews: mustQuery(root, "[data-global-views]"),
    freshness: mustQuery(root, "[data-freshness]"),
    areaPanel: mustQuery(root, "[data-area-panel]"),
    upNext: mustQuery(root, "[data-upnext]"),
    areaList: mustQuery(root, "[data-area-list]"),
    unmappedPanel: mustQuery(root, "[data-unmapped-panel]"),
    unmatchedPanel: mustQuery(root, "[data-unmatched-panel]"),
    staleBanner: mustQuery(root, "[data-stale-banner]"),
    healthPanel: mustQuery(root, "[data-health-panel]"),
    apiError: mustQuery(root, "[data-api-error]"),
    retryButton: mustQuery(root, "[data-retry-button]"),
  };
}

const LEGEND_SUMMARY_TIERS: readonly GlanceTier[] = ["off", "soon", "later", "clear"] as const;

/** R-623: the collapsed desktop legend still shows the tier counts as coloured dots. */
export function renderLegendSummary(container: HTMLElement, counts: Partial<Record<GlanceCondition, number>>): void {
  container.textContent = "";
  const tiers: GlanceTier[] = [...LEGEND_SUMMARY_TIERS];
  if ((counts.unknown ?? 0) > 0) {
    tiers.push("unknown");
  }
  for (const tier of tiers) {
    const item = document.createElement("span");
    item.className = "legend-summary-item";
    item.dataset.tier = tier;
    const label = document.createElement("span");
    label.className = "sr-only";
    label.textContent = `${CONDITION_METADATA[tier].label}: `;
    item.appendChild(label);
    item.appendChild(document.createTextNode(String(counts[tier] ?? 0)));
    container.appendChild(item);
  }
}

export function updateLegendCounts(root: QueryRoot, counts: Partial<Record<GlanceCondition, number>>): void {
  for (const condition of GLANCE_CONDITIONS) {
    const count = root.querySelector<HTMLElement>(`[data-status-count="${condition}"]`);
    if (count) {
      count.textContent = String(counts[condition] ?? 0);
    }
    const row = root.querySelector<HTMLElement>(`[data-status-row="${condition}"]`);
    row?.classList.toggle("is-empty", (counts[condition] ?? 0) === 0);
  }
  const summary = root.querySelector<HTMLElement>("[data-legend-summary]");
  if (summary) {
    renderLegendSummary(summary, counts);
  }
}

export function setLegendOpen(legend: HTMLElement, open: boolean): void {
  legend.dataset.legendOpen = open ? "true" : "false";
  legend.querySelector<HTMLButtonElement>("[data-legend-toggle]")?.setAttribute("aria-expanded", open ? "true" : "false");
}

export function isLegendOpen(legend: HTMLElement): boolean {
  return legend.dataset.legendOpen === "true";
}

/** R-626: "59 mapped · 11 unmapped" on the collapsed All areas disclosure. */
export function updateDirectoryCount(root: QueryRoot, status: StatusResponse | null): void {
  const target = root.querySelector<HTMLElement>("[data-area-directory-count]");
  if (!target) {
    return;
  }
  if (!status) {
    target.textContent = "";
    return;
  }
  const mapped = status.areas.filter((area) => area.mapped).length;
  const unmapped = status.areas.length - mapped;
  target.textContent = unmapped > 0 ? `${mapped} mapped · ${unmapped} unmapped` : `${mapped} mapped`;
}

export function checkedStatusFilters(root: QueryRoot): Set<GlanceCondition> {
  const checked = new Set<GlanceCondition>();
  for (const input of root.querySelectorAll<HTMLInputElement>("[data-status-filter]")) {
    if (input.checked) {
      checked.add(input.value as GlanceCondition);
    }
  }
  return checked;
}

/** R-629: nothing selected means every area shows; one or more selected shows only those conditions. */
export function effectiveStatusFilters(root: QueryRoot): Set<GlanceCondition> {
  const checked = checkedStatusFilters(root);
  return checked.size === 0 ? new Set<GlanceCondition>(GLANCE_CONDITIONS) : checked;
}

/** Mirrors the filter state onto the legend so CSS can dim unselected rows and reveal the clear control. */
export function syncFilterState(root: QueryRoot): boolean {
  const active = checkedStatusFilters(root).size > 0;
  const legend = root.querySelector<HTMLElement>("[data-legend]");
  if (legend) {
    legend.dataset.filterActive = active ? "true" : "false";
  }
  const clear = root.querySelector<HTMLElement>("[data-legend-clear]");
  if (clear) {
    clear.hidden = !active;
  }
  return active;
}

export function clearStatusFilters(root: QueryRoot): void {
  for (const input of root.querySelectorAll<HTMLInputElement>("[data-status-filter]")) {
    input.checked = false;
  }
  syncFilterState(root);
}

/* ---------- Sheet summary / peek card ---------- */

const SHEET_SUMMARY_TIERS = ["off", "soon", "later"] as const;
const SHEET_SUMMARY_WORDS: Record<(typeof SHEET_SUMMARY_TIERS)[number], string> = {
  off: "off now",
  soon: "soon",
  later: "later",
};

export interface SheetSelection {
  name: string;
  statusLabel: string;
  tier?: GlanceTier;
  countdown?: { kind: "starts_in" | "ends_in"; target: string };
}

function countsSummaryText(counts: Partial<Record<GlanceCondition, number>>): string {
  return SHEET_SUMMARY_TIERS.map((tier) => `${counts[tier] ?? 0} ${SHEET_SUMMARY_WORDS[tier]}`).join(" · ");
}

export function formatSheetSummary(
  counts: Partial<Record<GlanceCondition, number>>,
  selected?: SheetSelection,
  now = new Date(),
): string {
  if (selected) {
    const parts = [selected.name, selected.statusLabel];
    if (selected.countdown) {
      const short = formatCountdownShort(now, new Date(selected.countdown.target), selected.countdown.kind);
      if (short) {
        parts.push(short);
      }
    }
    return parts.join(" · ");
  }
  return `${countsSummaryText(counts)} · Tap an area or search to see your status`;
}

function ensureSummaryParts(summary: HTMLElement): { name: HTMLElement; status: HTMLElement } {
  let name = summary.querySelector<HTMLElement>("[data-sheet-summary-name]");
  let status = summary.querySelector<HTMLElement>("[data-sheet-summary-status]");
  if (!name || !status) {
    summary.textContent = "";
    name = document.createElement("span");
    name.className = "sheet-summary-name";
    name.dataset.sheetSummaryName = "";
    status = document.createElement("span");
    status.className = "sheet-summary-status";
    status.dataset.sheetSummaryStatus = "";
    summary.appendChild(name);
    summary.appendChild(status);
  }
  return { name, status };
}

function sheetSeparator(leading: boolean): HTMLElement {
  const separator = document.createElement("span");
  separator.className = leading ? "sheet-sep sheet-sep-leading" : "sheet-sep";
  separator.textContent = " · ";
  return separator;
}

export function updateSheetSummary(
  summary: HTMLElement,
  counts: Partial<Record<GlanceCondition, number>>,
  selected?: SheetSelection,
  now = new Date(),
): void {
  const parts = ensureSummaryParts(summary);
  const card = summary.closest<HTMLElement>("[data-sheet-summary-toggle]");
  if (!selected) {
    parts.name.textContent = "";
    SHEET_SUMMARY_TIERS.forEach((tier, index) => {
      if (index > 0) {
        parts.name.appendChild(document.createTextNode(" · "));
      }
      const chip = document.createElement("span");
      chip.className = "peek-count";
      chip.dataset.tier = tier;
      chip.textContent = `${counts[tier] ?? 0} ${SHEET_SUMMARY_WORDS[tier]}`;
      parts.name.appendChild(chip);
    });
    parts.status.textContent = "";
    parts.status.appendChild(sheetSeparator(true));
    parts.status.appendChild(document.createTextNode("Tap an area or search to see your status"));
    card?.removeAttribute("data-tier");
    return;
  }

  parts.name.textContent = selected.name;
  const pill = document.createElement("span");
  pill.className = "tier-pill tier-pill-small";
  if (selected.tier) {
    pill.dataset.tier = selected.tier;
  }
  pill.textContent = selected.statusLabel;
  parts.status.textContent = "";
  parts.status.appendChild(sheetSeparator(true));
  parts.status.appendChild(pill);
  if (selected.countdown) {
    const short = formatCountdownShort(now, new Date(selected.countdown.target), selected.countdown.kind);
    if (short) {
      parts.status.appendChild(document.createTextNode(` · ${short}`));
    }
  }
  if (card) {
    if (selected.tier) {
      card.dataset.tier = selected.tier;
    } else {
      card.removeAttribute("data-tier");
    }
  }
}

/* ---------- Area directory (All areas disclosure) ---------- */

export function featureDisplayName(feature: AreaFeature): string {
  return feature.properties.names?.[0] ?? feature.properties.label ?? feature.properties.key;
}

export function cityOfAreaName(name: string): string {
  const portion = /Portion of ([^:]+):/.exec(name);
  if (portion?.[1]) {
    return portion[1].trim();
  }
  const trailing = /,\s*([^,]+)$/.exec(name);
  if (trailing?.[1]) {
    return trailing[1].trim();
  }
  return "Other areas";
}

const OPEN_GROUP_TIERS: readonly GlanceTier[] = ["off", "soon"] as const;
const GROUP_COUNT_TIERS = ["off", "soon", "later"] as const;

interface DirectoryRow {
  feature: AreaFeature;
  name: string;
  city: string;
  glance: Glance;
}

function compareDirectoryRows(a: DirectoryRow, b: DirectoryRow): number {
  const tierDelta = GLANCE_ORDER[a.glance.tier] - GLANCE_ORDER[b.glance.tier];
  if (tierDelta !== 0) {
    return tierDelta;
  }
  const aTarget = a.glance.countdown?.target ?? "";
  const bTarget = b.glance.countdown?.target ?? "";
  if (aTarget !== bTarget) {
    return aTarget < bTarget ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
}

function shortRelative(now: Date, target: Date): string {
  const diffMs = target.getTime() - now.getTime();
  const totalMinutes = Math.max(1, Math.round(Math.abs(diffMs) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }
  if (hours > 0) {
    parts.push(`${hours} hr`);
  }
  if (days === 0 && minutes > 0) {
    parts.push(`${minutes} min`);
  }
  const span = parts.join(" ");
  return diffMs >= 0 ? `in ${span}` : `${span} ago`;
}

export function appendGlanceTiming(parent: HTMLElement, glance: Glance, now: Date): HTMLElement | null {
  if (!glance.countdown) {
    return null;
  }
  const timing = document.createElement("span");
  timing.className = "rel-time area-when";
  timing.dataset.relTarget = glance.countdown.target;
  timing.dataset.relKind = glance.countdown.kind;
  const target = new Date(glance.countdown.target);
  const span = shortRelative(now, target);
  const verb = glance.countdown.kind === "ends_in" ? (target > now ? "Ends" : "Ended") : target > now ? "Starts" : "Started";
  timing.textContent = `${verb} ${span}`;
  parent.appendChild(timing);
  return timing;
}

function createAreaRow(row: DirectoryRow, now: Date): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "area-list-item";
  button.dataset.areaKey = row.feature.properties.key;
  button.dataset.tier = row.glance.tier;
  button.setAttribute("role", "listitem");
  button.title = row.name;

  const name = document.createElement("span");
  name.className = "area-name";
  name.textContent = row.name;
  button.appendChild(name);
  if (row.feature.properties.provisional) {
    appendEstimatedTag(button);
  }

  const meta = document.createElement("span");
  meta.className = "area-meta";

  const status = document.createElement("span");
  status.className = "area-status tier-pill";
  status.dataset.status = row.feature.properties.stale
    ? row.feature.properties.lastKnownStatus
    : row.feature.properties.status;
  status.dataset.tier = row.glance.tier;
  status.textContent = row.glance.label;
  meta.appendChild(status);
  appendGlanceTiming(meta, row.glance, now);

  button.appendChild(meta);
  return button;
}

function appendEstimatedTag(parent: HTMLElement): HTMLSpanElement {
  const tag = document.createElement("span");
  tag.className = "estimated-tag";
  tag.textContent = "Estimated";
  parent.appendChild(tag);
  return tag;
}

function appendExternalLink(parent: HTMLElement, url: string, label: string): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = url;
  link.textContent = label;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  parent.appendChild(link);
  return link;
}

export function renderAreaList(
  container: HTMLElement,
  features: AreaFeature[],
  filters: Set<GlanceCondition>,
  query = "",
  now: Date = new Date(),
): void {
  container.textContent = "";
  const needle = query.trim().toLowerCase();

  const rows: DirectoryRow[] = [];
  for (const feature of features) {
    const glance = computeGlance(feature.properties, now);
    if (!matchesFilters(glance, filters)) {
      continue;
    }
    const name = featureDisplayName(feature);
    const city = cityOfAreaName(name);
    if (needle && !name.toLowerCase().includes(needle) && !city.toLowerCase().includes(needle)) {
      continue;
    }
    rows.push({ feature, name, city, glance });
  }

  if (rows.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = needle ? `No areas match "${query.trim()}".` : "No areas match the current filters.";
    container.appendChild(empty);
    return;
  }

  const groups = new Map<string, DirectoryRow[]>();
  for (const row of rows) {
    const group = groups.get(row.city);
    if (group) {
      group.push(row);
    } else {
      groups.set(row.city, [row]);
    }
  }

  const cities = [...groups.keys()].sort((a, b) => a.localeCompare(b));
  for (const city of cities) {
    const groupRows = groups.get(city) ?? [];
    groupRows.sort(compareDirectoryRows);

    const counts: Record<GlanceTier, number> = { off: 0, soon: 0, later: 0, clear: 0, unknown: 0 };
    for (const row of groupRows) {
      counts[row.glance.tier] += 1;
    }

    const details = document.createElement("details");
    details.className = "area-group";
    details.dataset.city = city;
    const worst = GLANCE_TIERS.find((tier) => counts[tier] > 0) ?? "clear";
    details.dataset.tier = worst;
    details.open = Boolean(needle) || OPEN_GROUP_TIERS.some((tier) => counts[tier] > 0);

    const summary = document.createElement("summary");
    const title = document.createElement("span");
    title.className = "area-group-title";
    title.textContent = `${city} · ${groupRows.length} ${groupRows.length === 1 ? "area" : "areas"}`;
    summary.appendChild(title);
    const badges = document.createElement("span");
    badges.className = "area-group-badges";
    for (const tier of GROUP_COUNT_TIERS) {
      if (counts[tier] > 0) {
        const badge = document.createElement("span");
        badge.className = "tier-dot-count";
        badge.dataset.tier = tier;
        badge.textContent = `${counts[tier]} ${SHEET_SUMMARY_WORDS[tier]}`;
        badges.appendChild(badge);
      }
    }
    summary.appendChild(badges);

    const list = document.createElement("div");
    list.className = "area-list-inner";
    list.setAttribute("role", "list");
    for (const row of groupRows) {
      list.appendChild(createAreaRow(row, now));
    }

    details.appendChild(summary);
    details.appendChild(list);
    container.appendChild(details);
  }
}

export function renderUnmappedAreas(
  container: HTMLElement,
  status: StatusResponse,
  names: ReadonlyMap<string, string> = new Map(),
  now: Date = new Date(),
): void {
  container.textContent = "";
  const heading = document.createElement("h2");
  heading.textContent = "Unmapped areas";
  container.appendChild(heading);

  const unmapped = status.areas.filter((area) => !area.mapped);
  if (unmapped.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "No unmapped advisories.";
    container.appendChild(empty);
    return;
  }

  const note = document.createElement("p");
  note.className = "muted";
  note.textContent = "Mentioned in advisories but not drawable yet.";
  container.appendChild(note);

  const list = document.createElement("ol");
  list.className = "unmapped-list";
  const sorted = [...unmapped].sort((a, b) => {
    const delta = GLANCE_ORDER[computeGlance(a, now).tier] - GLANCE_ORDER[computeGlance(b, now).tier];
    return delta !== 0 ? delta : (names.get(a.key) ?? a.key).localeCompare(names.get(b.key) ?? b.key);
  });
  for (const area of sorted) {
    const glance = computeGlance(area, now);
    const row = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = "area-list-item";
    button.dataset.areaKey = area.key;
    button.dataset.tier = glance.tier;
    const name = document.createElement("span");
    name.className = "area-name";
    name.textContent = names.get(area.key) ?? area.key;
    const meta = document.createElement("span");
    meta.className = "area-meta";
    const pill = document.createElement("span");
    pill.className = "area-status tier-pill";
    pill.dataset.tier = glance.tier;
    pill.textContent = glance.label;
    meta.appendChild(pill);
    appendGlanceTiming(meta, glance, now);
    const unmappedNote = document.createElement("span");
    unmappedNote.className = "area-unmapped";
    unmappedNote.textContent = "No polygon yet";
    meta.appendChild(unmappedNote);
    button.appendChild(name);
    button.appendChild(meta);
    row.appendChild(button);
    if (area.mapUrl) {
      appendExternalLink(row, area.mapUrl, "Open VECO's map");
    }
    list.appendChild(row);
  }
  container.appendChild(list);
}

export function mergeStatusIntoFeatures(features: AreaFeature[], status: StatusResponse): AreaFeature[] {
  const byKey = new Map<string, StatusAreaSummary>();
  for (const area of status.areas) {
    byKey.set(area.key, area);
  }

  return features.map((feature) => {
    const summary = byKey.get(feature.properties.key);
    if (!summary) {
      return feature;
    }
    return {
      ...feature,
      properties: {
        ...feature.properties,
        status: summary.status,
        stale: summary.stale,
        lastKnownStatus: summary.lastKnownStatus,
        label: summary.label,
        activeWindow: summary.activeWindow,
        nextWindow: summary.nextWindow,
        // R-674: geojson is the source of truth for estimated-area fields.
        provisional: feature.properties.provisional,
        mapUrl: feature.properties.mapUrl,
        tracerConfidence: feature.properties.tracerConfidence,
      },
    };
  });
}

/* ---------- Freshness, stale banner, health ---------- */

export function latestAdvisoryAt(status: StatusResponse | null | undefined): string | null {
  let latest: string | null = null;
  for (const area of status?.areas ?? []) {
    if (area.lastUpdateAt && (latest === null || area.lastUpdateAt > latest)) {
      latest = area.lastUpdateAt;
    }
  }
  return latest;
}

function appendAgo(parent: HTMLElement, iso: string | null, now: Date): void {
  const time = document.createElement("time");
  time.className = "rel-time";
  if (iso) {
    time.dateTime = iso;
    time.dataset.relTarget = iso;
    time.dataset.relKind = "ago";
    time.title = formatManilaDateTime(new Date(iso));
    time.textContent = formatRelativeTime(iso, now);
  } else {
    time.textContent = "never";
  }
  parent.appendChild(time);
}

/** R-615: "Checked VECO's page 4 minutes ago · Latest advisory 2 hours ago". */
export function renderFreshness(
  container: HTMLElement,
  health: HealthResponse | null,
  status: StatusResponse | null,
  now: Date = new Date(),
): void {
  container.textContent = "";

  const checked = document.createElement("span");
  checked.className = "freshness-item";
  checked.dataset.freshnessChecked = "";
  checked.appendChild(document.createTextNode("Checked "));
  const pageLink = document.createElement("a");
  pageLink.href = VECO_FACEBOOK_PAGE_URL;
  pageLink.target = "_blank";
  pageLink.rel = "noopener noreferrer";
  pageLink.textContent = "VECO's page";
  checked.appendChild(pageLink);
  checked.appendChild(document.createTextNode(" "));
  appendAgo(checked, health?.poller.lastSuccessAt ?? null, now);
  container.appendChild(checked);

  const separator = document.createElement("span");
  separator.className = "freshness-sep";
  separator.setAttribute("aria-hidden", "true");
  separator.textContent = " · ";
  container.appendChild(separator);

  const latest = document.createElement("span");
  latest.className = "freshness-item";
  latest.dataset.freshnessLatest = "";
  latest.appendChild(document.createTextNode("Latest advisory "));
  appendAgo(latest, latestAdvisoryAt(status), now);
  container.appendChild(latest);
}

export function updateStaleBanner(banner: HTMLElement, health: HealthResponse): void {
  banner.textContent = "";
  if (!health.stale) {
    banner.setAttribute("hidden", "");
    return;
  }

  const message = document.createElement("span");
  message.textContent = `Updates delayed: last successful Facebook check was ${formatRelativeTime(health.poller.lastSuccessAt)}.`;
  const link = document.createElement("a");
  link.href = "#health-panel";
  link.textContent = "Health details";
  banner.appendChild(message);
  banner.appendChild(document.createTextNode(" "));
  banner.appendChild(link);
  banner.removeAttribute("hidden");
}

export function renderHealthPanel(container: HTMLElement, health: HealthResponse, now = new Date()): void {
  container.textContent = "";
  const heading = document.createElement("h2");
  heading.textContent = "Health details";
  const status = document.createElement("p");
  status.textContent = health.ok ? "API health is OK." : "API health has warnings.";
  const fetch = document.createElement("p");
  const lastSuccess = health.poller.lastSuccessAt;
  fetch.textContent = lastSuccess
    ? `Last successful Facebook check: ${formatRelativeTime(lastSuccess, now)} (${formatManilaDateTime(new Date(lastSuccess))})`
    : "Last successful Facebook check: never";
  const failures = document.createElement("p");
  failures.textContent = `Consecutive failures: ${health.poller.consecutiveFailures}`;
  container.appendChild(heading);
  container.appendChild(status);
  container.appendChild(fetch);
  container.appendChild(failures);
}
